import { LegalPage } from "../../components/LegalPage";

// S50 — Antibiotic Stewardship Program methodology.
// Trust hub disclosure of regulatory scope, clinical reference sources,
// and FDA SaMD positioning. Updated when the underlying rule set or CDC
// Core Elements release changes.

export function AspMethodologyPage() {
  return (
    <LegalPage
      title="Antibiotic Stewardship Program methodology"
      eyebrow="Trust center"
      description="WeConsult Rx Antibiotic Stewardship Program (ASP) module methodology, regulatory scope (42 CFR 483.80(a)(4)), cited clinical guidance (CDC, SHEA/IDSA, McGeer, Loeb), and product positioning."
      path="/trust/asp-methodology"
      effective="2026-05-31"
    >
      <p>
        The Antibiotic Stewardship Program (ASP) module supports SNF
        compliance with the CMS Mega-Rule Phase 3 requirement at
        42 CFR &sect;483.80(a)(4): each facility must establish and maintain
        an antibiotic stewardship program that includes antibiotic use
        protocols and a system to monitor antibiotic use. The module
        operates as documentation, surveillance, and reporting support for
        the facility's designated ASP lead pharmacist and physician
        champion.
      </p>

      <h2>Regulatory scope</h2>
      <ul>
        <li>
          <strong>42 CFR &sect;483.80(a)(4)</strong> &mdash; Infection
          Prevention and Control Program; Antibiotic Stewardship Program.
        </li>
        <li>
          <strong>F-tag F880</strong> &mdash; Infection Prevention &amp;
          Control.
        </li>
        <li>
          <strong>F-tag F881</strong> &mdash; Antibiotic Stewardship Program.
        </li>
      </ul>

      <h2>Cited clinical references</h2>
      <p>
        Each criterion surfaced in the module carries a verbatim source
        citation in both the UI and the database schema. The module does
        not synthesize new criteria.
      </p>
      <ul>
        <li>
          <strong>CDC Core Elements of Antibiotic Stewardship for
          Nursing Homes</strong> (2015, with subsequent updates).{" "}
          <a
            href="https://www.cdc.gov/antibiotic-use/healthcare/implementation/core-elements-nursing-homes.html"
            target="_blank"
            rel="noreferrer"
          >
            CDC publication
          </a>
          . Drives the dashboard KPI set (empiric-with-culture %,
          de-escalation rate, mean duration, class diversification).
        </li>
        <li>
          <strong>SHEA / IDSA / PIDS White Paper:</strong> Antibiotic
          Stewardship Programs in Long-Term Care Facilities.{" "}
          <em>Infect Control Hosp Epidemiol</em> 2019.
        </li>
        <li>
          <strong>McGeer Criteria</strong> for surveillance definitions of
          infection in LTC (Stone ND et al., revised 2012).{" "}
          <em>Infect Control Hosp Epidemiol</em> 2012;33(10):965-77.
        </li>
        <li>
          <strong>Loeb Minimum Criteria</strong> for initiation of
          antibiotics in residents of LTC. Loeb M, et al.{" "}
          <em>Infect Control Hosp Epidemiol</em> 2001;22(2):120-4.
          Operationalized as a per-indication checklist for UTI courses
          (separate tracks for non-catheterized and catheterized residents).
        </li>
        <li>
          <strong>SBAR communication tool</strong> &mdash; AHRQ TeamSTEPPS
          endorsed structured communication framework, surfaced as an
          auto-generated template when Loeb criteria are not documented.
        </li>
      </ul>

      <h2>Per-facility metric definitions</h2>
      <ul>
        <li>
          <strong>Total courses</strong> &mdash; count of antibiotic
          courses with a start_date within the selected period.
        </li>
        <li>
          <strong>Days of Therapy (DOT) per 1,000 resident-days</strong>{" "}
          &mdash; standard CDC NHSN AUR module metric. Calculation
          requires resident-day census; the dashboard shows &ldquo;&mdash;&rdquo;
          when the census source is not yet configured for the facility.
        </li>
        <li>
          <strong>Empiric with culture %</strong> &mdash; share of courses
          where a culture was obtained at or before initiation. CDC core
          element: &ldquo;perform culture and susceptibility before starting
          antibiotics where feasible.&rdquo;
        </li>
        <li>
          <strong>De-escalation rate</strong> &mdash; share of
          culture-obtained courses subsequently narrowed based on
          susceptibility (SHEA/IDSA/PIDS 2019 denominator).
        </li>
        <li>
          <strong>Asymptomatic bacteriuria treatments</strong> &mdash;
          should approach zero. Loeb 2001 and IDSA 2019 explicitly
          recommend against treating asymptomatic bacteriuria in LTC
          residents.
        </li>
        <li>
          <strong>Fluoroquinolone %</strong> &mdash; high-risk class
          tracked separately per FDA boxed warnings (tendinopathy, QT
          prolongation, CNS effects).
        </li>
        <li>
          <strong>Mean duration</strong> &mdash; average days for completed
          courses. Target: shortest effective duration.
        </li>
        <li>
          <strong>C. difficile courses</strong> &mdash; count of courses
          coded as C. diff treatment or where C. diff was logged as an
          adverse event.
        </li>
      </ul>

      <h2>Product positioning (FDA SaMD line)</h2>
      <p>
        The ASP module is positioned as an <strong>educational and
        documentation tool</strong> that supports the facility&rsquo;s
        Antibiotic Stewardship Program. It is not a clinical decision
        support system that diagnoses, prescribes, or selects therapy.
      </p>
      <ul>
        <li>
          The pharmacist ASP lead and physician champion retain full
          clinical decision authority.
        </li>
        <li>
          Criteria checklists (Loeb, McGeer) surface verbatim source
          definitions to support documentation only. The module does not
          accept or reject orders.
        </li>
        <li>
          Dashboard metrics aggregate logged courses. They do not generate
          alerts that interrupt clinical workflows.
        </li>
        <li>
          Facility ASP policy &mdash; including the empiric treatment
          protocols stored under <code>asp_facility_policy</code> &mdash;
          remains the source of truth for prescribing guidance at the
          facility level.
        </li>
      </ul>

      <h2>PHI safeguards</h2>
      <ul>
        <li>
          Antibiotic course rows are PHI. Row-Level Security restricts
          SELECT/INSERT/UPDATE/DELETE to the owning tenant via the
          <code>current_tenant_id()</code> claim. Cross-tenant access is
          impossible by construction.
        </li>
        <li>
          Audit metadata for course-related events carries only:
          facility_id, abx_class, indication, count summaries, and a
          duration band (&lt;5, 5-7, 8-10, &gt;10 days). It does not carry
          drug name, dose, organism, resident name, or MRN.
        </li>
        <li>
          The pharmacist comment and de-escalation reason fields may
          contain resident-identifying narrative. These fields render only
          inside the per-course review drawer body and are never copied to
          the audit log.
        </li>
      </ul>

      <h2>Change control</h2>
      <p>
        Updates to clinical criteria (CDC Core Elements revisions, IDSA
        guideline updates, McGeer criteria revisions) trigger a versioned
        update to this page, the corresponding UI text, and the
        <code>antibiotic_courses</code> schema where applicable. The
        effective date at the top of this page reflects the most recent
        revision.
      </p>
    </LegalPage>
  );
}
